import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import type { DeepThoughtsResult } from "@somethings-ui/core/deep-thoughts";
import type {
  HotTakesConfig,
  HotTakesResult,
} from "@somethings-ui/core/hot-takes";
import type {
  TeenHotTakesConfig,
  TeenHotTakesResult,
} from "@/lib/activities/hot-takes-shapes";
import { useToast } from "@/components/ui/use-toast";
import { fetchApi } from "./index";

// ─── Types ───

export type ActivityType = "hot_takes" | "deep_thoughts" | "connect_moment";

export type ActivityStatus = "sent" | "in_progress" | "completed" | "declined";

export interface ActivityDefinition {
  label: string;
  description: string;
  allowDecline: boolean;
  renderMode: "native" | "webview";
}

export interface ConnectActivityMentorAnswer {
  question_id: string;
  library_answer_id: string;
  audio_s3_key: string | null;
  duration_ms: number | null;
  transcript: string | null;
}

export interface ConnectActivityConfig {
  cardset_id: string;
  mentor_answers: Array<ConnectActivityMentorAnswer | null>;
}

export interface ConnectActivityTeenVoiceResponse {
  mode: "voice";
  audio_s3_key: string;
  duration_ms?: number;
  transcript?: string | null;
}

export interface ConnectActivityTeenTextResponse {
  mode: "text";
  content: string;
}

export type ConnectActivityTeenResponse =
  | ConnectActivityTeenVoiceResponse
  | ConnectActivityTeenTextResponse;

export interface ConnectActivityLevelResult {
  teen_response: ConnectActivityTeenResponse | null;
  revealed_at: string | null;
  advanced_at: string | null;
}

export interface ConnectActivityResult {
  levels: Array<ConnectActivityLevelResult | null>;
}

interface BaseActivity {
  id: string;
  status: ActivityStatus;
  initiator?: "mentor" | "teen";
  isExpired: boolean;
  expiresAt: string | null;
  openedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  mentorshipId: string;
  takedown?: boolean;
  takedownAlertId?: string | null;
  activityDefinition: ActivityDefinition | null;
}

export type DeepThoughtsPrerequisitesStatus = {
  hotTakesCompleted: number;
  connectMomentCompleted: number;
  allMet: boolean;
};

export interface HotTakesActivity extends BaseActivity {
  activityType: "hot_takes";
  config: HotTakesConfig | TeenHotTakesConfig;
  result: HotTakesResult | TeenHotTakesResult | null;
}

export interface ConnectActivity extends BaseActivity {
  activityType: "connect_moment";
  config: ConnectActivityConfig;
  result: ConnectActivityResult | null;
}

export interface DeepThoughtsActivity extends BaseActivity {
  activityType: "deep_thoughts";
  config: Record<string, unknown> | null;
  result: DeepThoughtsResult | null;
}

export type Activity =
  | HotTakesActivity
  | ConnectActivity
  | DeepThoughtsActivity;

export type ActivityApiResponse =
  | Exclude<Activity, ConnectActivity>
  | (Omit<ConnectActivity, "config" | "result"> & {
      config?: unknown;
      result?: unknown;
    });

const connectMentorAnswerSchema = z.object({
  question_id: z.string(),
  library_answer_id: z.string(),
  // nullish so legacy rows missing optional keys still normalize.
  audio_s3_key: z.string().nullish(),
  duration_ms: z.number().nullish(),
  transcript: z.string().nullish(),
});

const connectTeenResponseSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("voice"),
    audio_s3_key: z.string(),
    duration_ms: z.number().optional(),
    transcript: z.string().nullable().optional(),
  }),
  z.object({
    mode: z.literal("text"),
    content: z.string(),
  }),
]);

const connectLevelResultSchema = z.object({
  teen_response: connectTeenResponseSchema.nullish(),
  revealed_at: z.string().nullish(),
  advanced_at: z.string().nullish(),
});

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function normalizeConnectConfig(value: unknown): ConnectActivityConfig {
  const config = asRecord(value);
  const mentorAnswers = Array.isArray(config.mentor_answers)
    ? config.mentor_answers.slice(0, 3).map((answer) => {
        const parsed = connectMentorAnswerSchema.safeParse(answer);
        if (!parsed.success) return null;
        return {
          question_id: parsed.data.question_id,
          library_answer_id: parsed.data.library_answer_id,
          audio_s3_key: parsed.data.audio_s3_key ?? null,
          duration_ms: parsed.data.duration_ms ?? null,
          transcript: parsed.data.transcript ?? null,
        };
      })
    : [];

  return {
    cardset_id: typeof config.cardset_id === "string" ? config.cardset_id : "",
    mentor_answers: mentorAnswers,
  };
}

function normalizeConnectResult(value: unknown): ConnectActivityResult | null {
  if (value === null || value === undefined) return null;
  const levels = asRecord(value).levels;
  return {
    levels: Array.isArray(levels)
      ? levels.slice(0, 3).map((level) => {
          if (level === null) return null;
          const parsed = connectLevelResultSchema.safeParse(level);
          if (!parsed.success) return null;
          return {
            teen_response: parsed.data.teen_response ?? null,
            revealed_at: parsed.data.revealed_at ?? null,
            advanced_at: parsed.data.advanced_at ?? null,
          };
        })
      : [],
  };
}

export function normalizeActivityApiResponse(
  activity: ActivityApiResponse,
): Activity {
  if (activity.activityType !== "connect_moment") return activity;
  // Teen-initiated shape ({question_ids, author_answers} / {responder_answers})
  // has no legacy rows to repair; normalizing would strip it to mentor shape.
  if ("question_ids" in asRecord(activity.config)) {
    return activity as ConnectActivity;
  }
  return {
    ...activity,
    config: normalizeConnectConfig(activity.config),
    result: normalizeConnectResult(activity.result),
  };
}

export const isHotTakesActivity = (
  activity: Activity | undefined,
): activity is HotTakesActivity => activity?.activityType === "hot_takes";

export const isConnectActivity = (
  activity: Activity | undefined,
): activity is ConnectActivity => activity?.activityType === "connect_moment";

export const isDeepThoughtsActivity = (
  activity: Activity | undefined,
): activity is DeepThoughtsActivity =>
  activity?.activityType === "deep_thoughts";

export interface AvailableActivity {
  type: ActivityType;
  label: string;
  description: string;
  enabled: boolean;
  reason: string | null;
  isTeenEligible: boolean;
  prereqs?: DeepThoughtsPrerequisitesStatus;
}

export interface AvailabilityResponse {
  activities: AvailableActivity[];
  weeklyUsed: number;
  weeklyLimit: number;
  sentToday: boolean;
  customSwapPrompt: { isTeenEligible: boolean };
}

interface SendActivityResponse {
  entity: {
    id: string;
    entityType: string;
    activity: {
      activity_type: ActivityType;
      status: ActivityStatus;
    };
  };
}

// ─── Query Keys ───

export const ACTIVITY_QUERY_KEY = "activity";
export const ACTIVITY_AVAILABILITY_KEY = "activity-availability";
export const ACTIVITY_DEFINITION_KEY = "activity-definition";

export const useActivityDefinition = <TDefinition>(
  activityType: ActivityType,
) => {
  return useQuery<TDefinition>({
    queryKey: [ACTIVITY_DEFINITION_KEY, activityType],
    queryFn: async () => {
      const response = await fetchApi<TDefinition>({
        url: `/v2/activities/definitions/${activityType}`,
        method: "GET",
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 60,
  });
};

export interface ActivityUploadPresignedUrl {
  s3Key: string;
  presignedUrl: string;
  contentType: string;
}

export const mintActivityUploadUrl = async (
  entityId: string,
  purpose: string,
  contentType: string,
): Promise<ActivityUploadPresignedUrl> => {
  const response = await fetchApi<ActivityUploadPresignedUrl>({
    url: `/v2/activities/${entityId}/uploads/presigned-url`,
    method: "POST",
    data: { purpose, contentType },
  });
  return response.data;
};

// ─── Hooks ───

interface ActivityQueryOptions {
  retryOnMount?: boolean;
  staleTime?: number;
}

export const useActivity = (
  entityId: string,
  queryOptions?: ActivityQueryOptions,
) => {
  return useQuery<Activity>({
    queryKey: [ACTIVITY_QUERY_KEY, entityId],
    queryFn: async () => {
      const response = await fetchApi<ActivityApiResponse>({
        url: `/v2/activities/${entityId}`,
        method: "GET",
      });
      return normalizeActivityApiResponse(response.data);
    },
    enabled: !!entityId,
    retry: false,
    retryOnMount: queryOptions?.retryOnMount,
    staleTime: queryOptions?.staleTime,
  });
};

export const useConnectActivity = (entityId: string) => {
  const query = useActivity(entityId);
  return {
    ...query,
    data: isConnectActivity(query.data) ? query.data : undefined,
  };
};

export const useAvailableActivities = (mentorshipId: string) => {
  return useQuery<AvailabilityResponse>({
    queryKey: [ACTIVITY_AVAILABILITY_KEY, mentorshipId],
    queryFn: async () => {
      const response = await fetchApi<AvailabilityResponse>({
        url: `/v2/activities/available/${mentorshipId}`,
        method: "GET",
      });
      return response.data;
    },
    enabled: !!mentorshipId,
  });
};

interface SendActivityVariables {
  mentorshipId: string;
  activityType: ActivityType;
  config?: HotTakesConfig | Record<string, unknown>;
}

interface UpdateActivityStatusVariables {
  entityId: string;
  status: ActivityStatus;
  result?: Record<string, unknown>;
}

export const useUpdateActivityStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<Activity, Error, UpdateActivityStatusVariables>({
    mutationKey: ["activity-status"],
    mutationFn: async ({ entityId, status, result }) => {
      const response = await fetchApi<Activity>({
        url: `/v2/activities/${entityId}/status`,
        method: "PATCH",
        data: { status, ...(result ? { result } : {}) },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ACTIVITY_QUERY_KEY, variables.entityId],
      });
      queryClient.invalidateQueries({ queryKey: ["shareable-entities"] });
      queryClient.invalidateQueries({ queryKey: ["engagement-summary"] });
      queryClient.invalidateQueries({ queryKey: ["tool-completion-counts"] });
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      const message =
        error?.response?.data?.error || "Failed to send your answers";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });
};

export const useSendActivity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<SendActivityResponse, Error, SendActivityVariables>({
    mutationKey: ["activity-send"],
    mutationFn: async ({ mentorshipId, activityType, config }) => {
      const response = await fetchApi<SendActivityResponse>({
        url: "/v2/activities/send",
        method: "POST",
        data: {
          mentorshipId,
          activityType,
          ...(config ? { config } : {}),
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Activity sent",
        description: "The activity has been sent to the teen.",
      });
      queryClient.invalidateQueries({
        queryKey: [ACTIVITY_AVAILABILITY_KEY, variables.mentorshipId],
      });
      // History list + calendar aggregate + Tools counts read from these caches.
      queryClient.invalidateQueries({ queryKey: ["shareable-entities"] });
      queryClient.invalidateQueries({ queryKey: ["engagement-summary"] });
      queryClient.invalidateQueries({ queryKey: ["tool-completion-counts"] });
      // Action suggestion + notice opportunity badge read notice_signals off mentorship.
      queryClient.invalidateQueries({ queryKey: ["mentorship"] });
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      const message = error?.response?.data?.error || "Failed to send activity";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });
};
